import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import SEO from "@/components/site/SEO";
import { DecorationField, Star } from "@/components/site/Decorations";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowRight, Compass, CheckCircle2 } from "lucide-react";
import { IMG } from "@/lib/siteData";
import { PRESCHOOL_PROGRAMS } from "@/lib/programData";

function ProgramBreakdown({ p, index }) {
  return (
    <div className="grid lg:grid-cols-5 gap-8" data-testid={`program-${p.key}`}>
      {/* Left sidebar */}
      <div className="lg:col-span-2 lg:sticky lg:top-28 self-start">
        <div className={`rounded-[2rem] p-8 bg-gradient-to-br ${p.color} text-white shadow-lg relative overflow-hidden`}>
          <div aria-hidden className="absolute -top-8 -right-8 opacity-15">
            <Star color="#fff" size={120} />
          </div>
          <span className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full bg-white/25`}>
            Program {String(index + 1).padStart(2, "0")}
          </span>
          <h3 className="mt-3 font-display text-3xl md:text-4xl font-bold leading-tight">
            {p.label}
          </h3>
          <div className="mt-1 text-white/85 text-sm font-semibold tracking-wide">{p.age}</div>

          <div className="mt-6 space-y-4 text-[15px] text-white/90 leading-relaxed">
            {p.intro.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      </div>

      {/* Bullets */}
      <div className="lg:col-span-3">
        <p className="text-gray-700 text-lg font-semibold">{p.listLabel}</p>
        <ul className="mt-6 space-y-4">
          {p.items.map((item, i) => (
            <li
              key={item.title}
              className="flex gap-4 bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
            >
              <span className={`h-9 w-9 shrink-0 rounded-xl ${p.pill} flex items-center justify-center`}>
                <CheckCircle2 className="h-5 w-5" />
              </span>
              <div>
                <h4 className="font-display text-lg font-semibold text-gray-900 leading-snug">
                  {item.title}
                </h4>
                <p className="mt-1 text-[15px] text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            </li>
          ))}
        </ul>
        <div className={`mt-8 rounded-2xl p-5 ${p.pill}`}>
          <p className="text-[15.5px] font-semibold leading-relaxed">{p.outro}</p>
        </div>
        {p.learnMoreTo && (
          <div className="mt-8">
            <p className="text-[15px] font-semibold text-gray-700">
              Want the full 2K daily schedule and how to apply?
            </p>
            <Button
              asChild
              size="lg"
              data-testid="learn-more-2k"
              className="mt-3 bg-brand-green hover:bg-brand-green-deep text-white rounded-full px-7 py-6 text-base font-semibold shadow-lg hover:-translate-y-0.5 transition-transform"
            >
              <Link to={p.learnMoreTo}>
                Learn More <ArrowRight className="h-5 w-5 ml-1" />
              </Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PreschoolPage() {
  return (
    <div data-testid="preschool-page" className="overflow-hidden">
      <SEO
        title="Preschool Program"
        description="Explore Innovators Prep's 2K, 3K, and Pre-K programs in Brooklyn, NY — play-based, developmentally appropriate learning for ages 12 months to 5."
      />
      <PageHero title="Preschool Program" crumb="Preschool Program" />

      {/* Welcome + collage */}
      <section className="relative py-20 md:py-28">
        <DecorationField variant="mixed" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative grid grid-cols-6 grid-rows-6 gap-3 h-[520px]">
              <img
                src={IMG.preschoolCollage[0]}
                alt="Children playing with colourful blocks together in the classroom"
                className="col-span-4 row-span-4 rounded-[2rem] object-cover w-full h-full shadow-lg"
              />
              <img
                src={IMG.preschoolCollage[1]}
                alt="Preschool learning activity at Innovators Prep"
                className="col-span-2 row-span-3 rounded-[2rem] object-cover w-full h-full shadow-lg col-start-5"
              />
              <img
                src={IMG.preschoolCollage[2]}
                alt="Pre-school child focused on building a creation"
                className="col-span-3 row-span-2 rounded-[2rem] object-cover w-full h-full shadow-lg row-start-5"
              />
              <div className="col-span-3 row-span-2 rounded-[2rem] bg-gradient-to-br from-brand-green-soft to-brand-purple-soft shadow-inner flex items-center justify-center row-start-5 col-start-4">
                <span className="font-display text-4xl font-bold text-brand-purple-deep">Play.</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <span className="inline-block px-3 py-1 rounded-full bg-brand-green-soft text-brand-green-deep text-xs font-bold tracking-widest uppercase">
              Ages 12 months — 5
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Welcome to our early childhood <span className="text-brand-green">classroom</span>
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed">
              Where we create a nurturing and engaging environment that fosters exploration and
              creativity through play-based learning. Our curriculum incorporates high-quality
              materials that spark curiosity and encourage hands-on experiences, enabling children to
              develop critical thinking skills in a fun and supportive atmosphere. With a focus on
              interactive play, our dedicated educators guide each child as they explore their
              interests, build relationships, and learn important social skills, all while laying the
              groundwork for lifelong learning.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Programs breakdown — 2K / 3K / Pre-K tabs */}
      <section className="relative py-20 md:py-28 bg-brand-cream" data-testid="program-tabs-section">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="text-center max-w-2xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full bg-brand-purple-soft text-brand-purple-deep text-xs font-bold tracking-widest uppercase">
              Our Programs
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              A dedicated program for each <span className="text-brand-purple">age group</span>
            </h2>
            <p className="mt-4 text-gray-600 text-lg">
              Explore how our classrooms are thoughtfully designed for two-, three-, and four-year-olds.
            </p>
          </Reveal>

          <Tabs defaultValue="2k" className="mt-12">
            <TabsList
              className="mx-auto flex flex-wrap h-auto justify-center gap-2 bg-white/80 backdrop-blur ring-1 ring-black/[0.05] rounded-full p-1.5 w-fit"
              data-testid="program-tabs"
            >
              {PRESCHOOL_PROGRAMS.map((p) => (
                <TabsTrigger
                  key={p.key}
                  value={p.key}
                  data-testid={`tab-${p.key}`}
                  className="rounded-full px-5 py-2 text-sm font-bold text-gray-600 hover:text-gray-900 data-[state=active]:bg-gradient-to-r data-[state=active]:from-brand-green data-[state=active]:to-brand-green-deep data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
                >
                  {p.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {PRESCHOOL_PROGRAMS.map((p, i) => (
              <TabsContent key={p.key} value={p.key} className="mt-12 focus-visible:ring-0">
                <Reveal>
                  <ProgramBreakdown p={p} index={i} />
                </Reveal>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Our Approach teaser */}
      <section className="relative py-20 md:py-24">
        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="bg-white rounded-[2.5rem] shadow-md border border-gray-100 p-8 md:p-12 flex flex-col md:flex-row gap-8 items-center text-center md:text-left">
              <div className="h-20 w-20 shrink-0 rounded-3xl bg-gradient-to-br from-brand-green to-brand-purple text-white flex items-center justify-center shadow-md">
                <Compass className="h-9 w-9" />
              </div>
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-brand-purple-soft text-brand-purple-deep text-xs font-bold tracking-widest uppercase">
                  Our Approach
                </span>
                <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold text-gray-900">
                  Individualized, collaborative, and joyful
                </h3>
                <p className="mt-3 text-gray-600 leading-relaxed">
                  Every classroom moment is shaped by four pillars — individualized instruction, a
                  collaborative team, deep family engagement, and warm social-emotional support.
                  Explore how it all comes together and how we support each age group.
                </p>
              </div>
              <Button
                asChild
                data-testid="preschool-approach-cta"
                className="bg-brand-green hover:bg-brand-green-deep text-white rounded-full px-7 py-6 font-semibold shrink-0"
              >
                <Link to="/approach">See Our Approach <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
